import React, { useEffect, useState } from "react";
import { db } from "../../firebase"; // Adjust path to your firebase config
import {
    collection, addDoc, getDocs, updateDoc,
    deleteDoc, doc, query, orderBy, serverTimestamp
} from "firebase/firestore";
import {
    Box, Typography, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Button, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Chip, Stack, InputAdornment, TablePagination,
    CircularProgress
} from "@mui/material";
import { Plus, Search, Edit3, Trash2, LayoutGrid, CheckCircle, XCircle } from "lucide-react";
import Swal from "sweetalert2";

const Insurancecat = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // Form State
    const [formData, setFormData] = useState({ id: null, category: "", status: "Active" });

    const categoriesRef = collection(db, "insurance_categories");

    // Fetch Data
    const fetchCategories = async () => {
        setLoading(true);
        try {
            const q = query(categoriesRef, orderBy("createdAt", "desc"));
            const snap = await getDocs(q);
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setCategories(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchCategories(); }, []);

    const handleSubmit = async () => {
        if (!formData.category.trim()) {
            return Swal.fire("Error", "Category name is required", "error");
        }

        try {
            if (formData.id) {
                await updateDoc(doc(db, "insurance_categories", formData.id), {
                    category: formData.category,
                    status: formData.status,
                    updatedAt: serverTimestamp() // ✅ add this
                });
            } else {
                await addDoc(categoriesRef, {
                    category: formData.category,
                    status: formData.status,
                    createdAt: serverTimestamp()
                });
            }

            setOpen(false);
            setFormData({ id: null, category: "", status: "Active" });

            await fetchCategories(); // ✅ ensure refresh after update

            Swal.fire("Success", `Category ${formData.id ? 'updated' : 'added'} successfully`, "success");

        } catch (err) {
            console.error(err);
            Swal.fire("Error", err.message, "error");
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This action cannot be undone!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            confirmButtonText: "Yes, delete it!"
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, "insurance_categories", id));
                await fetchCategories(); // ✅ refresh after delete

                Swal.fire("Deleted!", "Category has been removed.", "success");
            } catch (err) {
                console.error(err);
                Swal.fire("Error", "Delete failed", "error");
            }
        }
    };

    // Filtering Logic
    const filteredData = categories.filter(item =>
        item.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box sx={{ p: 1, bgcolor: "#f8fafc", minHeight: "100vh" }}>
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Box>
                    <Typography variant="h5" fontWeight={800} color="#1e293b" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LayoutGrid size={24} color="#6366f1" /> Insurance Categories
                    </Typography>
                    <Typography variant="body2" color="textSecondary">Manage your insurance types and availability</Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Plus size={18} />}
                    onClick={() => { setFormData({ id: null, category: "", status: "Active" }); setOpen(true); }}
                    sx={{ borderRadius: 2, bgcolor: "#6366f1", textTransform: "none", fontWeight: 600 }}
                >
                    Add New Category
                </Button>
            </Stack>

            {/* Search & Table */}
            <Paper sx={{ borderRadius: 4, overflow: "hidden", border: "1px solid #e2e8f0", boxShadow: "none" }}>
                <Box sx={{ p: 2, borderBottom: "1px solid #e2e8f0" }}>
                    <TextField
                        placeholder="Search categories..."
                        size="small"
                        fullWidth
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search size={18} color="#94a3b8" />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: 2, bgcolor: "#f1f5f9" }
                        }}
                    />
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: "#f8fafc" }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, color: "#64748b" }}>Sr. No</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: "#64748b" }}>Category Name</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: "#64748b" }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: "#64748b" }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={30} sx={{ my: 2 }} /></TableCell></TableRow>
                            ) : filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>{row.category}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.status}
                                            size="small"
                                            icon={row.status === "Active" ? <CheckCircle size={14} /> : <XCircle size={14} />}
                                            sx={{
                                                bgcolor: row.status === "Active" ? "#f0fdf4" : "#fef2f2",
                                                color: row.status === "Active" ? "#16a34a" : "#dc2626",
                                                fontWeight: 700
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => {
                                            setFormData({
                                                id: row.id,
                                                category: row.category || "",
                                                status: row.status || "Active"
                                            });
                                            setOpen(true);
                                        }} sx={{ color: "#6366f1" }}>
                                            <Edit3 size={18} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(row.id)} sx={{ color: "#ef4444" }}>
                                            <Trash2 size={18} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>

            {/* Add/Edit Modal */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>{formData.id ? "Edit Category" : "New Insurance Category"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            label="Category Name"
                            fullWidth
                            value={formData.category}
                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        />
                        <TextField
                            select
                            label="Status"
                            fullWidth
                            SelectProps={{ native: true }}
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        >
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                        </TextField>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpen(false)} sx={{ color: "#64748b", textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} sx={{ bgcolor: "#6366f1", borderRadius: 2, textTransform: 'none', px: 4 }}>
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Insurancecat;